/**
 * Canonical API envelope helpers.
 *
 * Goal: provide a single reusable place for `status/status_code + entity|entities`
 * wrappers, without changing existing endpoint DTOs.
 *
 * Existing reference implementation lives in:
 * - types/endpoints/auth.ts (ApiEnvelope<T> + unwrapEnvelope)
 */

export type ApiStatus = "success" | "error" | (string & {});

/** Generic envelope where payload is placed in `entity`. */
export interface ApiEnvelope<T> {
  status: ApiStatus;
  status_code: string;
  entity: T;
}

/** Generic envelope where payload is placed in `entities` (lists/pagination). */
export interface ApiEntitiesEnvelope<T> {
  status: ApiStatus;
  status_code: string;
  entities: T;
}

export type AnyApiEnvelope<TEntity, TEntities> =
  | ApiEnvelope<TEntity>
  | ApiEntitiesEnvelope<TEntities>;

export const isApiEnvelope = <T = unknown>(data: unknown): data is ApiEnvelope<T> =>
  typeof data === "object" &&
  data !== null &&
  "status" in data &&
  "status_code" in data &&
  "entity" in data;

export const isApiEntitiesEnvelope = <T = unknown>(
  data: unknown
): data is ApiEntitiesEnvelope<T> =>
  typeof data === "object" &&
  data !== null &&
  "status" in data &&
  "status_code" in data &&
  "entities" in data;

/**
 * Unwrap `entity` envelope.
 * Supports plain payload for endpoints that return unwrapped DTOs.
 */
export const unwrapEntity = <T>(data: T | ApiEnvelope<T>): T =>
  isApiEnvelope<T>(data) ? data.entity : (data as T);

/**
 * Unwrap `entities` envelope.
 * Supports plain payload for endpoints that return unwrapped DTOs.
 */
export const unwrapEntities = <T>(data: T | ApiEntitiesEnvelope<T>): T =>
  isApiEntitiesEnvelope<T>(data) ? data.entities : (data as T);